import { Link } from 'react-router-dom';
import { Button } from '@/design-system/ui/Button';
import { Card } from '@/design-system/ui/Card';
import { Icon } from '@/design-system/ui/Icon';
import { Reveal } from '@/design-system/ui/Reveal';
import { useSEO } from '@/core/seo/useSEO';
import { SITE_NAME, SITE_URL } from '@/core/config/content';
import doorImg from '@/assets/images/door_to_door.png';

const ADVANTAGES = [
  {
    icon: 'pin' as const,
    title: 'سوار شدن از درب مبدأ',
    text: 'نیازی به مراجعه به پایانه یا ایستگاه نیست؛ راننده سواری شما را از درب منزل یا محل کار در شهر مبدأ سوار می‌کند.',
  },
  {
    icon: 'route' as const,
    title: 'پیاده شدن در آدرس مقصد',
    text: 'مستقیماً در نشانی دقیق مورد نظرتان در شهر مقصد پیاده می‌شوید؛ بدون هزینه اضافی برای تاکسی‌های درون‌شهری.',
  },
  {
    icon: 'shield' as const,
    title: 'سفر اختصاصی و امن',
    text: 'خودرو سواری استاندارد، رانندگان تأیید صلاحیت‌شده و تجربه سفری آرام، تمیز و بدون معطلی برای تکمیل ظرفیت.',
  },
];

const STEPS = [
  { step: '۱', title: 'تعیین نشانی مبدأ و مقصد', desc: 'نشانی دقیق خود در شهر مبدأ و مقصد را مشخص کنید.' },
  { step: '۲', title: 'هماهنگی و تعیین ساعت', desc: 'ساعت حرکت دلخواه و مشخصات سفر را انتخاب کنید.' },
  { step: '۳', title: 'حرکت از در به در', desc: 'خودرو در زمان تعیین‌شده حاضر بوده و شما را مستقیماً به مقصد می‌رساند.' },
];

const JSON_LD = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Service',
      name: 'سفر درب‌به‌درب بین‌شهری بلینو',
      serviceType: 'Door to Door Intercity Transportation',
      provider: {
        '@type': 'Organization',
        name: SITE_NAME,
        url: SITE_URL,
      },
      description: 'سفر بین‌شهری اختصاصی از درب خانه در مبدأ تا درب آدرس در مقصد با ناوگان سواری بین‌شهری بلینو.',
      areaServed: {
        '@type': 'AdministrativeArea',
        name: 'ایران',
      },
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: SITE_NAME, item: `${SITE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'سفر درب‌به‌درب', item: `${SITE_URL}/door-to-door` },
      ],
    },
  ],
};

export default function DoorToDoorPage() {
  useSEO({
    title: 'سفر درب‌به‌درب بین‌شهری',
    description: 'سفر بین‌شهری درب‌به‌درب بلینو؛ از درب خانه تا مقصد با سواری اختصاصی بین‌شهری، بدون معطلی و بدون نیاز به مراجعه به ترمینال.',
    canonical: '/door-to-door',
    keywords: 'سفر درب به درب بین شهری, تاکسی بین شهری درب به درب, سواری بین شهری اختصاصی, تاکسی دربستی بین شهری, بلینو',
    jsonLd: JSON_LD,
  });

  return (
    <div className="mx-auto max-w-4xl px-4 py-8 md:py-12">
      {/* Hero Section */}
      <section className="relative overflow-hidden rounded-[24px] bg-hero p-6 text-white shadow-card md:p-10">
        <div className="pointer-events-none absolute -end-16 -top-16 size-64 sm:animate-aurora rounded-full bg-[radial-gradient(circle,rgb(2_195_154/0.3),transparent_70%)]" aria-hidden />
        <div className="relative grid items-center gap-6 md:grid-cols-[1.2fr_0.8fr]">
          <div>
            <span className="inline-block rounded-full bg-white/10 px-3 py-1 text-xs font-bold text-mint backdrop-blur-sm">
              نسل نوین سفرهای جاده‌ای
            </span>
            <h1 className="mt-3 text-2xl font-bold leading-snug md:text-3xl">
              سفر بین‌شهری درب‌به‌درب
              <br />
              <span className="text-shine">از درِ مبدأ، تا درِ مقصد</span>
            </h1>
            <p className="mt-3 text-sm leading-relaxed text-white/85 md:text-base">
              با سرویس درب‌به‌درب بلینو، دیگر نیازی به مراجعه به پایانه و هماهنگی چند تاکسی درون‌شهری نیست. خودرو سواری اختصاصی شما را از جلوی درب سوار کرده و دقیقاً به مقصد می‌رساند.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/#search">
                <Button size="lg" className="bg-cta text-white hover:bg-cta-hover">
                  خرید بلیط سواری بین‌شهری
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="secondary" className="border-white/30 text-white hover:bg-white/10">
                  درخواست رزرو ویژه
                </Button>
              </Link>
            </div>
          </div>
          <div className="flex justify-center">
            <img src={doorImg} alt="سفر درب به درب بلینو" className="max-h-56 w-auto object-contain drop-shadow-xl" />
          </div>
        </div>
      </section>

      {/* Advantages */}
      <section className="mt-12" aria-labelledby="advantages-heading">
        <Reveal>
          <h2 id="advantages-heading" className="text-xl font-bold">
            چرا سفر درب‌به‌درب بلینو؟
          </h2>
          <p className="mt-1 text-sm text-ink-2">مزایای سفر مستقیم جاده‌ای با خودرو سواری</p>
        </Reveal>
        <div className="mt-6 grid gap-4 sm:grid-cols-3">
          {ADVANTAGES.map((adv, idx) => (
            <Reveal key={adv.title} delay={idx * 100}>
              <Card className="flex h-full flex-col p-5">
                <span className="grid size-12 place-items-center rounded-xl bg-primary-light text-navy">
                  <Icon name={adv.icon} size={22} />
                </span>
                <h3 className="mt-4 font-bold text-ink-1">{adv.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-ink-2">{adv.text}</p>
              </Card>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Steps */}
      <section className="mt-12" aria-labelledby="steps-heading">
        <Reveal>
          <h2 id="steps-heading" className="text-xl font-bold">
            مراحل سفر درب‌به‌درب
          </h2>
        </Reveal>
        <div className="mt-6 grid gap-4 sm:grid-cols-3">
          {STEPS.map((s, idx) => (
            <Reveal key={s.step} delay={idx * 120}>
              <Card className="p-5">
                <span className="grid size-10 place-items-center rounded-full bg-cta text-base font-bold text-white shadow-glow">
                  {s.step}
                </span>
                <h3 className="mt-3 font-bold">{s.title}</h3>
                <p className="mt-1 text-sm text-ink-2">{s.desc}</p>
              </Card>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FAQ Block for GEO & AEO */}
      <section className="mt-12 rounded-[20px] bg-paper p-6 shadow-sm">
        <h2 className="text-lg font-bold">سوالات متداول درباره سفر درب‌به‌درب</h2>
        <div className="mt-4 space-y-4 text-sm">
          <div>
            <h3 className="font-bold text-navy">آیا می‌توانم سفر درب‌به‌درب را برای کل خانواده به صورت دربستی رزرو کنم؟</h3>
            <p className="mt-1 text-ink-2 leading-relaxed">
              بله، سرویس درب‌به‌درب به صورت اختصاصی ارائه می‌شود و کل ظرفیت خودرو در اختیار شما و همراهانتان خواهد بود.
            </p>
          </div>
          <div className="border-t border-line pt-3">
            <h3 className="font-bold text-navy">هزینه سفر درب‌به‌درب چگونه محاسبه می‌شود؟</h3>
            <p className="mt-1 text-ink-2 leading-relaxed">
              هزینه بر اساس مسافت بین دو نقطه مبدأ و مقصد و تعرفه رسمی ناوگان بین‌شهری تعیین می‌شود و پیش از سفر به صورت شفاف به شما اعلام می‌گردد.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
